import { useState, useContext, useMemo } from "react";
import { AuthContext } from "../../context/auth-context";
import axios from "axios";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Alert from "@mui/material/Alert";
import { useDispatch } from "react-redux";
import { alertActions } from "../../../store/alert";

export const PartPropertyUpdate = () => {
  const [partID, setPartID] = useState("");
  const [property, setProperty] = useState("");
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const auth = useContext(AuthContext);
  const dispatch = useDispatch();

  const requestConfig = useMemo(
    () => ({
      headers: {
        Authorization: "Bearer " + auth.token,
      },
    }),
    [auth.token]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setMessage(null);
    setError(null);

    try {
      console.log("Submitting part property update:", { partID, property, value });
      
      const response = await axios.patch(
        "/api/parts/updateproperty",
        {
          partID: partID.trim(),
          property: property.trim(),
          value: value.trim() || "",
        },
        requestConfig
      );

      console.log("Part property updated successfully:", response.data);
      
      setMessage(
        `Property "${property}" successfully updated for part ${partID}`
      );
      
      // Clear form
      setPartID("");
      setProperty("");
      setValue("");
      
      dispatch(
        alertActions.showAlert({
          title: "Success",
          message: `Property "${property}" successfully updated for part ${partID}`,
        })
      );
    } catch (err) {
      console.error("Error updating part property:", err);
      const errorMessage =
        err.response?.data?.errorMessage ||
        err.message ||
        "There was an error updating the part property";
      setError(errorMessage);
      
      dispatch(
        alertActions.showAlert({
          title: "Error",
          message: errorMessage,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Update Part Property
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Update a property for a part in Stratus by entering the part ID and property details.
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            id="partID"
            label="Part ID"
            name="partID"
            autoComplete="off"
            autoFocus
            value={partID}
            onChange={(e) => setPartID(e.target.value)}
            disabled={loading}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            id="property"
            label="Property Name"
            name="property"
            autoComplete="off"
            value={property}
            onChange={(e) => setProperty(e.target.value)}
            disabled={loading}
            helperText="Enter the name of the property you want to update"
          />
          
          <TextField
            margin="normal"
            fullWidth
            id="value"
            label="Property Value (optional)"
            name="value"
            autoComplete="off"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={loading}
            helperText="Leave empty to set an empty value"
          />

          {message && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {message}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading || !partID.trim() || !property.trim()}
          >
            {loading ? "Updating..." : "Update Property"}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

